/**
 * THE TRASH (LIFECYCLE.md §3b) — networks this workspace deleted, each waiting for its purge date. Restore brings a
 * network back with its rooms exactly as they were; "Delete forever" (only where the workspace allows direct delete)
 * removes it now — type its name to confirm. Owners / admins (the delete roles). Backend: GET /networks?trash=1 ·
 * POST /networks/{id}/untrash · DELETE /networks/{id}?confirm=&forever=1.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { deleteNetwork, listTrashedNetworks, untrashNetwork } from '@stepflow/ui-shared/services/networkRestoreApi';
import { promptDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

/** PURE — "in 6 days" / "today" for a purge date. */
export function purgeWhen(iso, now = Date.now()) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '';
  const days = Math.ceil((t - now) / 86_400_000);
  return days <= 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`;
}

export default function NetworkTrashPanel({ query = '', showToast = null, onChanged = null }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => listTrashedNetworks().then(setState)
    .catch((e) => setState({ networks: [], policy: null, error: e?.data?.message || 'Could not load the Trash.' })), []);
  useEffect(() => { load(); }, [load]);
  if (!state) return <p className="px-5 py-6 text-xs text-gray-500" data-testid="trash-loading">Loading…</p>;
  const q = String(query || '').trim().toLowerCase();
  const rows = (state.networks || []).filter((n) => !q || `${n.name} ${n.networkId}`.toLowerCase().includes(q));
  const forever = !!state.policy?.deleteForever;

  const act = async (n, fn, done, { gone = false } = {}) => {
    setBusy(n.networkId);
    try {
      await fn();
      showToast?.(done, 'success');
      if (gone) {
        // the purge runs AFTER the reply (it took ~8 s in preprod) — a reload right now still lists it, so drop the
        // row the moment the server accepted the delete (L232)
        setState((st) => ({ ...st, networks: (st?.networks || []).filter((x) => x.networkId !== n.networkId) }));
      } else {
        await load();
      }
      await onChanged?.();
    } catch (e) {
      showToast?.(e?.data?.message || 'That didn’t work.', 'error');
    } finally {
      setBusy('');
    }
  };
  const deleteForever = async (n) => {
    const typed = await promptDialog({
      title: `Delete ${n.name} forever?`,
      message: 'The network, its rooms and all their work are removed now — this can’t be undone. Type the network name to confirm.',
      placeholder: n.name, confirmLabel: 'Delete forever', danger: true });
    if (typed == null) return;
    if (typed.trim() !== n.name) { showToast?.('The name didn’t match — nothing was deleted.', 'error'); return; }
    await act(n, () => deleteNetwork(n.networkId, { confirm: n.name, forever: true }),
      `${n.name} is being removed for good.`, { gone: true });
  };

  return (
    <div className="px-5 py-4" data-testid="network-trash">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><Trash2 className="h-4 w-4 text-gray-500" aria-hidden />Trash</h3>
      <p className="mt-1 text-xs text-gray-500">
        Deleted networks wait here with their rooms (frozen) until the date shown, then they&apos;re removed for good.
        {forever || state.error ? '' : ' This workspace protects its work — nothing here can be deleted early.'}
      </p>
      {state.error ? <p className="mt-3 text-xs text-rose-600">{state.error}</p> : null}
      {!rows.length ? (
        <p className="mt-4 text-xs text-gray-500" data-testid="trash-empty">The Trash is empty.</p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100 text-xs">
          {rows.map((n) => (
            <li key={n.networkId} className="flex items-center gap-2 py-2" data-testid={`trash-${n.networkId}`}>
              <span className="font-medium text-gray-800">{n.name}</span>
              <span className="text-gray-400">removed for good {purgeWhen(n.purgeAt)}</span>
              <span className="ml-auto inline-flex gap-1">
                <button type="button" disabled={!!busy} data-testid={`trash-restore-${n.networkId}`} aria-label={`Restore ${n.name}`}
                  onClick={() => act(n, () => untrashNetwork(n.networkId), `${n.name} is back.`)}
                  className={`${btn} inline-flex items-center gap-1 border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
                  {busy === n.networkId ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}Restore</button>
                {forever ? (
                  <button type="button" disabled={!!busy} data-testid={`trash-forever-${n.networkId}`} aria-label={`Delete ${n.name} forever`} onClick={() => deleteForever(n)}
                    className={`${btn} text-rose-700 hover:bg-rose-50 disabled:opacity-50`}>Delete forever</button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
